<template>
    <v-card>
        <v-card-title class="text-h5 bg-grey-lighten-2" primary-title>
            {{
                existingProtocol
                    ? `Edit Request Protocol ${existingProtocol.code}`
                    : "Create Request Protocol"
            }}
            <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
            <v-form @submit.prevent="save" v-model="isFormValid" ref="form">
                <v-row v-if="!existingProtocol">
                    <v-col cols="12">
                        <v-text-field
                            label="Internal SOP ID (optional)"
                            v-model="internalSOPId"
                            density="comfortable"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-text-field
                            label="Protocol Name"
                            v-model="name"
                            density="comfortable"
                            :rules="[(value) => !!value || 'Name is required']"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-autocomplete
                            label="Request Processor"
                            v-model="requestProcessorId"
                            :items="requestProcessors"
                            item-title="name"
                            item-value="id"
                            required
                            clearable
                        ></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <p>
                            Container types may be overridden when creating a request, and are not
                            needed for request protocols that are processed in external labs.
                        </p>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Default Source Samples Container Type"
                            v-model="samplesContainerTypeId"
                            :items="containerTypes"
                            item-title="name"
                            item-value="id"
                        ></v-autocomplete>
                    </v-col>

                    <v-col cols="6">
                        <v-autocomplete
                            label="Default Request Processing Container Type"
                            v-model="requestProcessingContainerTypeId"
                            :items="containerTypes"
                            item-title="name"
                            item-value="id"
                        ></v-autocomplete>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="cancel" color="primary">Cancel</v-btn>
            <v-btn @click="save" color="primary" :disabled="!isFormValid">Save</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup lang="ts">
import { ref } from "vue";
import {
    RequestProcessor,
    RequestProtocol,
    samplesAPI,
    SavedRequestProtocol,
} from "@/services/samples/samplesAPI";
import { withLoadingScreen } from "@/lib/loading";
import { ContainerType } from "@/services/commonModels";

const { existingProtocol, containerTypes, requestProcessors } = defineProps<{
    existingProtocol?: SavedRequestProtocol;
    containerTypes: ContainerType[];
    requestProcessors: RequestProcessor[];
}>();

const emit = defineEmits<{
    (e: "canceled"): void;
    (e: "requestProtocolSaved", protocol: RequestProtocol): void;
}>();

const internalSOPId = ref(existingProtocol ? existingProtocol.code : null);
const name = ref(existingProtocol ? existingProtocol.name : "");
const samplesContainerTypeId = ref<string | null>(
    existingProtocol ? existingProtocol.samplesContainerTypeId : null
);
const requestProcessingContainerTypeId = ref<string | null>(
    existingProtocol ? existingProtocol.requestContainerTypeId : null
);
const requestProcessorId = ref<string | null>(
    existingProtocol ? existingProtocol.requestProcessorId : null
);
const isFormValid = ref(false);

function cancel() {
    // reset form fields to existing protocol values or empty if no existing protocol
    internalSOPId.value = existingProtocol ? existingProtocol.code : null;
    name.value = existingProtocol ? existingProtocol.name : "";
    samplesContainerTypeId.value = existingProtocol
        ? existingProtocol.samplesContainerTypeId
        : null;
    requestProcessingContainerTypeId.value = existingProtocol
        ? existingProtocol.requestContainerTypeId
        : null;
    requestProcessorId.value = existingProtocol ? existingProtocol.requestProcessorId : null;
    isFormValid.value = false;
    emit("canceled");
}

async function save() {
    const protocol: RequestProtocol = {
        name: name.value,
        samplesContainerTypeId: samplesContainerTypeId.value,
        requestContainerTypeId: requestProcessingContainerTypeId.value,
        requestProcessorId: requestProcessorId.value,
        attributes: existingProtocol ? existingProtocol.attributes : [],
    };
    await withLoadingScreen(async () => {
        if (existingProtocol) {
            await samplesAPI.updateRequestProtocol(existingProtocol.id, protocol);
        } else {
            await samplesAPI.createRequestProtocol({ ...protocol, code: internalSOPId.value, });
        }
    });
    emit("requestProtocolSaved", protocol);
}
</script>
